import { FC, useEffect, useRef, useState } from 'react';
interface AnimatedNumberProps {
  value?: string | number;
  duration?: number;
  className?: string;
}
export const AnimatedNumber: FC<AnimatedNumberProps> = ({
  value,
  duration = 0.6,
  className
}) => {
  const str = String(value ?? '');
  const match = str.match(/^([^\d]*?)(\d[\d,]*\.?\d*)(.*)$/);
  if (!match || !match[2]) {
    return <span className={className}>{str}</span>;
  }
  const prefix = match[1];
  const numericStr = match[2].replace(/,/g, '');
  const suffix = match[3];
  const targetNum = parseFloat(numericStr);
  const shouldFormat = match[2].includes(',') || targetNum >= 1000;
  const decimalPlaces = numericStr.includes('.') ? numericStr.split('.')[1].length : 0;
  return <AnimatedValue prefix={prefix} targetNum={targetNum} suffix={suffix} shouldFormat={shouldFormat} decimalPlaces={decimalPlaces} duration={duration} className={className} data-sentry-element="AnimatedValue" data-sentry-component="AnimatedNumber" data-sentry-source-file="AnimatedNumber.tsx" />;
};
const AnimatedValue: FC<{
  prefix: string;
  targetNum: number;
  suffix: string;
  shouldFormat: boolean;
  decimalPlaces: number;
  duration: number;
  className?: string;
}> = ({
  prefix,
  targetNum,
  suffix,
  shouldFormat,
  decimalPlaces,
  duration,
  className
}) => {
  const [display, setDisplay] = useState('0');
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const format = (n: number) => {
      const fixed = decimalPlaces > 0 ? n.toFixed(decimalPlaces) : String(Math.round(n));
      return shouldFormat ? Number(fixed).toLocaleString(undefined, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces
      }) : fixed;
    };
    const el = ref.current;
    if (!el || targetNum === 0) {
      // Keep the requested precision for zero: "$0.00" must not render as "$0".
      setDisplay(targetNum === 0 ? format(0) : '');
      return;
    }
    let rafId: number;
    const runAnimation = () => {
      cancelAnimationFrame(rafId);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setDisplay(format(targetNum));
        return;
      }
      setDisplay('0');
      const startTime = performance.now();
      // A zero or negative duration jumps straight to the final value. A
      // negative durationMs would keep progress below 1 and schedule frames forever.
      const durationMs = Math.max(duration, 0) * 1000;
      const tick = (now: number) => {
        const elapsed = now - startTime;
        const progress = durationMs > 0 ? Math.min(elapsed / durationMs, 1) : 1;
        const eased = 1 - Math.pow(1 - progress, 3);
        setDisplay(format(eased * targetNum));
        if (progress < 1) {
          rafId = requestAnimationFrame(tick);
        }
      };
      rafId = requestAnimationFrame(tick);
    };
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        runAnimation();
      }
    }, {
      threshold: 0.5
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [targetNum, duration, shouldFormat, decimalPlaces]);
  return <span ref={ref} className={className} data-sentry-component="AnimatedValue" data-sentry-source-file="AnimatedNumber.tsx">
      {prefix}
      {display}
      {suffix}
    </span>;
};